import React, { useState } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { ReactComponent as ChevronDownIcon } from "feather-icons/dist/icons/chevron-down.svg";

const Subheading = tw(SubheadingBase)`mb-4 text-center`;
const Heading = tw(SectionHeading)`w-full`;
const Description = tw(SectionDescription)`w-full text-center`;

const Column = tw.div`flex flex-col items-center`;
const HeaderContent = tw.div``;

const FAQSContainer = tw.dl`mt-12 max-w-4xl relative`;
const FAQ = tw.div`cursor-pointer select-none mt-5 px-8 sm:px-10 py-5 sm:py-4 rounded-lg text-gray-800 hover:text-gray-900 bg-gray-200 hover:bg-gray-300 transition duration-300`;
const Question = tw.dt`flex justify-between items-center`;
const QuestionText = tw.span`text-lg lg:text-xl font-semibold`;
const QuestionToggleIcon = motion(styled.span`
  ${tw`ml-2 transition duration-300`}
  svg {
    ${tw`w-6 h-6`}
  }
`);
const Answer = motion(tw.dd`pointer-events-none text-sm sm:text-base leading-relaxed`);




export default ({
  subheading = "",
  heading = "You have Questions ?",
  description = "Straight answers about how EthosHub works.",
  faqs = [
    {
      question: "What is EthosHub?",
      answer: "EthosHub is a workplace incident reporting platform. Employees can report bullying, harassment, discrimination and other misconduct — anonymously or under their own name — and HR teams can investigate and resolve each case in one place."
    },
    {
      question: "Can incidents be reported anonymously?",
      answer: "Yes. Anonymous reporters don't create an account and aren't asked for any personal details. They receive a one-time access key, which they use to check progress and reply to the caseworker's questions."
    },
    {
      question: "What happens if an anonymous reporter loses their access key?",
      answer: "It can't be recovered. We store only a one-way hash of the key, never the key itself, so nobody — including EthosHub — can reissue it or link a report back to a person. The reporter can always submit a new report."
    },
    {
      question: "What if someone walks past while I'm writing a report?",
      answer: "Use Disguise Mode. Click the Hide button or press Esc twice and the page is instantly replaced with an ordinary weather forecast, including the browser tab title. Press Esc twice again, or click °C, to return — everything you've typed is still there."
    },
    {
      question: "What if a caseworker leaves a case open on their screen?",
      answer: "After two minutes without mouse or keyboard activity, EthosHub blurs the whole page so case details can't be read by anyone passing. Moving the mouse brings it straight back. It's a privacy screen rather than a lock, so caseworkers should still lock their computer when they step away."
    },
    {
      question: "Who can see incident reports?",
      answer: "Access is role-based. Caseworkers see the cases assigned to them, administrators manage the organisation, and leadership sees aggregated analytics only — reporter identity is never retrieved for those dashboards. Employees see only their own reports."
    },
    {
      question: "Where is our data stored?",
      answer: "In the United Kingdom. The database and file storage are hosted in the London (eu-west-2) region, and all traffic is encrypted in transit."
    },
    {
      question: "How does EthosHub support UK GDPR?",
      answer: "EthosHub is designed around data minimisation: anonymous reports carry no IP address, device information or account, and every case view and change is recorded in an audit log. As the data controller, your organisation remains responsible for its own compliance, and we're happy to help with your due diligence."
    },
    {
      question: "How are cases assigned to caseworkers?",
      answer: "Administrators and caseworkers assign and reassign cases from the case view. Every reassignment is recorded in the audit trail."
    },
    {
      question: "What analytics are available?",
      answer: "Leadership and administrators get a dashboard showing case volume over time, breakdowns by category, department and location, resolution times and open-versus-closed ratios."
    },
    {
      question: "Can we brand the platform?",
      answer: "Yes. You can add your logo, set your brand colour and write your own welcome message for the reporting portal."
    },
    {
      question: "How do we get started?",
      answer: "Book a demo and we'll walk you through the platform using a sample organisation, then help you set up your own."
    }
  ]
}) => {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);

  const toggleQuestion = questionIndex => {
    if (activeQuestionIndex === questionIndex) setActiveQuestionIndex(null);
    else setActiveQuestionIndex(questionIndex);
  };

  return (
    <Container>
      <ContentWithPaddingXl>
        <Column>
          <HeaderContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}
          </HeaderContent>
          <FAQSContainer>
            {faqs.map((faq, index) => (
              <FAQ
                key={index}
                onClick={() => {
                  toggleQuestion(index);
                }}
                className="group"
              >
                <Question>
                  <QuestionText>{faq.question}</QuestionText>
                  <QuestionToggleIcon
                    variants={{
                      collapsed: { rotate: 0 },
                      open: { rotate: -180 }
                    }}
                    initial="collapsed"
                    animate={activeQuestionIndex === index ? "open" : "collapsed"}
                    transition={{ duration: 0.02, ease: [0.04, 0.62, 0.23, 0.98] }}
                  >
                    <ChevronDownIcon />
                  </QuestionToggleIcon>
                </Question>
                <Answer
                  variants={{
                    open: { opacity: 1, height: "auto", marginTop: "16px" },
                    collapsed: { opacity: 0, height: 0, marginTop: "0px" }
                  }}
                  initial="collapsed"
                  animate={activeQuestionIndex === index ? "open" : "collapsed"}
                  transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                >
                  {faq.answer}
                </Answer>
              </FAQ>
            ))}
          </FAQSContainer>
        </Column>
      </ContentWithPaddingXl>
    </Container>
  );
};
